import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFeaturedArticles } from '@/mocks/notes';

export default function FeaturedArticles() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const articles = getFeaturedArticles();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const [featured, ...rest] = articles;

  return (
    <section ref={ref} className="py-16 md:py-24 bg-[#faf8f5] border-t border-[#ede5d8]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        {/* Section Label */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <div className="w-6 h-px bg-[#b08060]"></div>
            <span className="text-[0.8125rem] tracking-[0.2em] text-[#b08060] font-medium uppercase">精選文章</span>
          </div>
          <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#5a3e22]/8 border border-[#c8b49a]/60">
            <i className="ri-quill-pen-line text-[#7a6248] text-sm"></i>
            <span className="text-[0.8125rem] text-[#7a6248] font-medium">老師親選・推薦必讀</span>
          </div>
        </div>

        <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {/* Hero Featured Article */}
          {featured && (
            <Link to={`/notes/${featured.slug}`} className="group block mb-10 cursor-pointer">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-[#ede5d8] hover:border-[#c8b89a] transition-all duration-400">
                <div className="relative h-96 lg:h-auto overflow-hidden">
                  <img
                    src={featured.coverImage}
                    alt={featured.title}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-8 md:p-12 bg-white flex flex-col justify-center">
                  <div className="flex flex-wrap items-center gap-2 mb-5">
                    <span className="inline-block px-3 py-1 rounded-full bg-[#f0e8dc] text-[#7a6248] text-[0.8125rem] font-medium">
                      {featured.categoryLabel}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#5a3e22] text-white text-[0.75rem] font-medium">
                      <i className="ri-quill-pen-line text-[0.7rem]"></i>
                      老師推薦必讀
                    </span>
                  </div>
                  <h2 className="font-serif-tc text-[#2c2420] leading-[1.35] mb-4 group-hover:text-[#7a6248] transition-colors" style={{ fontSize: 'clamp(1.375rem, 2.5vw, 1.875rem)' }}>
                    {featured.title}
                  </h2>
                  <p className="text-[1.0625rem] leading-[1.75] text-[#7a6e62] mb-8 line-clamp-3">
                    {featured.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-[0.875rem] text-[#a89880]">
                      <span>{featured.publishedAt}</span>
                      <span>·</span>
                      <span>約 {featured.readTime} 分鐘</span>
                    </div>
                    <span className="text-[0.9375rem] text-[#7a6248] font-medium flex items-center gap-1.5 group-hover:gap-3 transition-all">
                      閱讀文章 <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* Secondary Featured Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((article, idx) => (
              <Link
                key={article.slug}
                to={`/notes/${article.slug}`}
                className="group block cursor-pointer"
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                <div className="rounded-xl overflow-hidden border border-[#ede5d8] hover:border-[#c8b89a] transition-all duration-300 bg-white h-full flex flex-col">
                  <div className="relative h-72 sm:h-80 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-3 right-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#5a3e22]/80 text-white text-[0.68rem] font-medium backdrop-blur-sm whitespace-nowrap">
                        <i className="ri-star-line text-[0.68rem]"></i>
                        老師推薦
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="inline-block px-2.5 py-1 rounded-full bg-[#f0e8dc] text-[#7a6248] text-[0.75rem] font-medium mb-4 w-fit">
                      {article.categoryLabel}
                    </span>
                    <h3 className="font-serif-tc text-[#2c2420] leading-[1.4] mb-3 group-hover:text-[#7a6248] transition-colors" style={{ fontSize: '1.125rem' }}>
                      {article.title}
                    </h3>
                    <p className="text-[0.9375rem] leading-[1.7] text-[#7a6e62] line-clamp-2 flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 mt-5 pt-4 border-t border-[#f0e8dc] text-[0.8125rem] text-[#a89880]">
                      <span>{article.publishedAt}</span>
                      <span>·</span>
                      <span>約 {article.readTime} 分鐘</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
